import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { ApiResponse } from '@classes/api-response';
import { MaterialOption, Settings } from '@models/settings';

export interface PricingSettings {
  dutyAmount: number;
  nosepadsInstallAmount: number;
  drillMountAmount: number;
  restockingFeePercent: number;
  salesTaxRate: number;
  materials: MaterialOption[];
}

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  private baseUrl = `${environment.base_url}/settings`;
  dutyAmount = 50;
  nosepadsInstallAmount = 0;
  drillMountAmount = 0;
  restockingFeePercent = 15;
  salesTaxRate = 0;
  materials: MaterialOption[] = [];

  constructor(private http: HttpClient) {}

  loadSettings(): Observable<Settings> {
    return this.http.get<ApiResponse<Settings>>(this.baseUrl).pipe(
      map((res) => new Settings(res.settings)),
      tap((settings) => this.applySettings(settings))
    );
  }

  updateDutyAmount(dutyAmount: number): Observable<Settings> {
    return this.updatePricingSettings({
      dutyAmount,
      nosepadsInstallAmount: this.nosepadsInstallAmount,
      drillMountAmount: this.drillMountAmount,
      restockingFeePercent: this.restockingFeePercent,
      salesTaxRate: this.salesTaxRate,
      materials: this.materials,
    });
  }

  updatePricingSettings(pricing: PricingSettings): Observable<Settings> {
    const materials = Array.isArray(pricing.materials)
      ? pricing.materials.map((item) => ({
          label: String(item?.label ?? '').trim(),
          price: Math.round(parseFloat(String(item?.price ?? 0)) * 100) / 100,
        }))
      : [];
    const body = {
      dutyAmount: pricing.dutyAmount,
      nosepadsInstallAmount: pricing.nosepadsInstallAmount,
      drillMountAmount: pricing.drillMountAmount,
      restockingFeePercent: pricing.restockingFeePercent,
      salesTaxRate: pricing.salesTaxRate,
      materials,
    };
    return this.http.put<ApiResponse<Settings>>(`${this.baseUrl}/pricing`, body).pipe(
      map((res) => {
        const settings = new Settings(res.settings);
        // Prefer server materials; if the API omits them, keep what we just saved.
        if (!Array.isArray(res?.settings?.materials)) {
          settings.materials = materials.map((item) => new MaterialOption(item));
        }
        return settings;
      }),
      tap((settings) => this.applySettings(settings))
    );
  }

  getRetailPlusDuty(retail: number | string): number {
    let value = typeof retail === 'string' ? parseFloat(retail.replace(/,/g, '')) : retail;
    value = isNaN(value) ? 0 : value;
    return value + this.dutyAmount;
  }

  getCustomAddOnTotal(options: {
    duty?: boolean;
    nosepadInstall?: boolean;
    drill?: boolean;
    materialPrice?: number | string | null;
  }): number {
    let total = 0;
    if (options.duty) {
      total += this.dutyAmount;
    }
    if (options.nosepadInstall) {
      total += this.nosepadsInstallAmount;
    }
    if (options.drill) {
      total += this.drillMountAmount;
    }
    if (options.materialPrice != null && options.materialPrice !== '') {
      const materialPrice =
        typeof options.materialPrice === 'string'
          ? parseFloat(options.materialPrice.replace(/,/g, ''))
          : options.materialPrice;
      if (!isNaN(materialPrice) && materialPrice > 0) {
        total += materialPrice;
      }
    }
    return total;
  }

  getMaterialPrice(label: string): number {
    const match = this.materials.find((item) => item.label === label);
    return match ? match.price : 0;
  }

  private applySettings(settings: Settings): void {
    this.dutyAmount = settings.dutyAmount;
    this.nosepadsInstallAmount = settings.nosepadsInstallAmount;
    this.drillMountAmount = settings.drillMountAmount;
    this.restockingFeePercent =
      settings.restockingFeePercent == null || isNaN(settings.restockingFeePercent)
        ? 15
        : settings.restockingFeePercent;
    this.salesTaxRate =
      settings.salesTaxRate == null || isNaN(settings.salesTaxRate)
        ? 0
        : settings.salesTaxRate;
    this.materials = Array.isArray(settings.materials) ? settings.materials : [];
  }
}
